import { initiateGam } from '@client/adManager/initiateGam.js';
import { initiateAdn } from '@client/adnuntius/initiateAdn.js';
import { setupEvents } from '@client/core/utils/events/index.js';
import { defineAdvtComponent } from '@client/core/components/webComponents/index.js';
import { sendErrorMessageMetricToServer } from '@client/core/services/metrics.js';
import {
  persistPPID,
  setPulsePageViewId
} from '@client/core/utils/pulse/pulse.js';
import {
  Brand,
  ConsentStatus,
  debugLog,
  DeviceType,
  type InitialState
} from '@schibsted-nmp/advertising-shared';
import { initiateHandleStickySkyscrapers } from '@client/core/utils/dom/handleStickySkyscrapers.js';
import { initiateHandleWallpaper } from '@client/core/utils/dom/handleWallpaper.js';
import {
  sendAmplitudeExperimentToGam,
  setupAmplitudeExperimentSubscription
} from '@client/core/utils/amplitude/index.js';

import { loadGlimrIfPermission } from './core/utils/glimr.js';
import './dev/AdvtDevTool';
import { $consentAtom } from './core/atoms/consent.js';

/**
 * Initiates ad rendering, setup page options and handles each placement.
 * @param initialState The initial state for setting up ads.
 */
export async function initiateAdvt(
  initialState: InitialState,
  ...abortSignals: ReadonlyArray<AbortSignal>
): Promise<void> {
  try {
    debugLog('Inside initiateAdvt()');

    const { brand, deviceType, adServer } = initialState.config;

    await Promise.all([
      persistPPID(),
      setPulsePageViewId(),
      loadGlimrIfPermission(brand)
    ]);

    setupEvents();

    if (deviceType === DeviceType.Desktop) {
      initiateHandleStickySkyscrapers();
    }

    initiateHandleWallpaper();
    defineAdvtComponent();

    // Send Amplitude experiment values to GAM and set up subscription for updates
    sendAmplitudeExperimentToGam();
    setupAmplitudeExperimentSubscription();

    const consent = $consentAtom.get();

    if (adServer.gam) {
      if (consent === ConsentStatus.Accepted || brand !== Brand.Dba) {
        await initiateGam(adServer.gam, ...abortSignals);
      }
    }

    if (adServer.adn && consent === ConsentStatus.Rejected) {
      await initiateAdn(adServer.adn);
    }

    debugLog('Advt initiated');
  } catch (error) {
    console.error('Failed to initiate ads', error);

    if (error instanceof Error) {
      try {
        await sendErrorMessageMetricToServer(
          `Failed to initiate ads ${error.message}`
        );

        debugLog('Sent error message metric to server');
      } catch (error: unknown) {
        console.error('Failed to send error message metric to server', error);
      }
    }
  }
}
